import { focusManager } from '@tanstack/react-query';
import { useEffect, type ReactNode } from 'react';

import { getPlatform } from './platform';
import { PlatformContext } from './PlatformContext';
import type { PlatformViewport } from './types';

function applyViewport(viewport: PlatformViewport): void {
  const root = document.documentElement.style;

  root.setProperty('--platform-viewport-height', `${viewport.height}px`);
  root.setProperty('--platform-safe-area-bottom', `${viewport.safeAreaInsetBottom}px`);
}

/**
 * Единая точка входа платформенного слоя в React-дерево.
 *
 * Экземпляр платформы создаётся один раз (getPlatform — синглтон) и
 * раздаётся через контекст: ниже по дереву код работает с общим
 * интерфейсом Platform, не зная, Web это, Telegram или VK.
 */
export function PlatformProvider({ children }: { children: ReactNode }) {
  // getPlatform() возвращает синглтон (см. platform.ts) — вызов на каждом
  // рендере дешёв и не пересоздаёт Telegram/VK-обвязку.
  const platform = getPlatform();

  useEffect(() => {
    platform.ready();
  }, [platform]);

  // Пока хост скрыл приложение (в VK — VKWebAppViewHide), react-query не
  // опрашивает сервер: refetchInterval стоит, возврат — как возврат фокуса.
  useEffect(() => {
    focusManager.setEventListener((handleFocus) => platform.subscribeVisibility(handleFocus));

    return () => focusManager.setEventListener(() => () => {});
  }, [platform]);

  useEffect(() => {
    applyViewport(platform.getViewport());
    return platform.subscribeViewport(applyViewport);
  }, [platform]);

  return <PlatformContext.Provider value={platform}>{children}</PlatformContext.Provider>;
}
